import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ArrowLeft, Search, Tag, Info, Loader, Edit, Trash2, Plus } from 'lucide-react';
import ImprovedUserExerciseForm from './ImprovedUserExerciseForm';
import { useTranslation } from 'react-i18next'; // Importiamo useTranslation

const UserExerciseManager = ({ onBack }) => {
  const { currentUser } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation(); // Estraiamo la funzione t
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/user_exercises_standalone.php?user_id=${currentUser.user_id}`);
      const data = await response.json();
      
      if (data.success) {
        setExercises(data.exercises);
      } else {
        setError(data.message || t('userExerciseManager.errors.loadError'));
      }
    } catch (error) {
      console.error('Errore:', error);
      setError(t('userExerciseManager.errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExercise = async (id) => {
    try {
      setLoading(true);
      const response = await fetch('/api/user_exercises_standalone.php', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ exercise_id: id }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setExercises(exercises.filter(ex => ex.id !== id));
        setConfirmDelete(null);
      } else {
        setError(data.message || t('userExerciseManager.errors.deleteError'));
      }
    } catch (error) {
      console.error('Errore:', error);
      setError(t('userExerciseManager.errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredExercises = exercises.filter(ex => 
    ex.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ex.gruppo_muscolare.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            {t('userExerciseManager.statusApproved')}
          </span>
        );
      case 'pending_review':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
            {t('userExerciseManager.statusPendingReview')}
          </span>
        );
      case 'user_only':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            {t('userExerciseManager.statusPrivate')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <button
        onClick={onBack}
        className="mb-6 flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        <span>{t('userExerciseManager.backToDashboard')}</span>
      </button>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('userExerciseManager.title')}
        </h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('userExerciseManager.newExercise')}
        </button>
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-blue-800 dark:text-blue-300 text-sm">
            <p className="font-medium">{t('userExerciseManager.infoTitle')}</p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>{t('userExerciseManager.infoPoints.0')}</li>
              <li>{t('userExerciseManager.infoPoints.1')}</li>
              <li>{t('userExerciseManager.infoPoints.2')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="relative mb-6">
        <input
          type="text"
          placeholder={t('userExerciseManager.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full p-3 pl-10 border rounded-lg ${
            isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-300'
          }`}
        />
        <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
      </div>

      {loading && exercises.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <Loader className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
          <p>{error}</p>
        </div>
      ) : filteredExercises.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery ? t('userExerciseManager.noExercisesFound') : t('userExerciseManager.noExercisesCreated')}
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t('userExerciseManager.clearSearch')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredExercises.map(exercise => (
            <div
              key={exercise.id}
              className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } shadow-sm`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {exercise.nome}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                      <Tag className="h-3.5 w-3.5 mr-1" />
                      {exercise.gruppo_muscolare}
                    </div>
                    {getStatusBadge(exercise.status)}
                    {exercise.is_isometric === 1 && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                        {t('userExerciseManager.isometric')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingExercise(exercise)}
                    className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    title={t('userExerciseManager.edit')}
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(exercise.id)}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title={t('userExerciseManager.delete')}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {exercise.descrizione && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {exercise.descrizione}
                </p>
              )}

              {exercise.attrezzatura && (
                <div className="mt-2 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{t('userExerciseManager.equipment')}: </span>
                  <span className="text-gray-600 dark:text-gray-400">{exercise.attrezzatura}</span>
                </div>
              )}

              {/* Conferma eliminazione */}
              {confirmDelete === exercise.id && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                    {t('userExerciseManager.deleteConfirmation')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-sm"
                    >
                      {t('userExerciseManager.cancel')}
                    </button>
                    <button
                      onClick={() => handleDeleteExercise(exercise.id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      {t('userExerciseManager.confirmDelete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form per aggiungere un nuovo esercizio */}
      {showAddForm && (
        <ImprovedUserExerciseForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            fetchExercises();
          }}
        />
      )}

      {/* Form per modificare un esercizio esistente */}
      {editingExercise && (
        <ImprovedUserExerciseForm
          exercise={editingExercise}
          onClose={() => setEditingExercise(null)}
          onSuccess={() => {
            setEditingExercise(null);
            fetchExercises();
          }}
        />
      )}
    </div>
  );
};

export default UserExerciseManager;